import { type FC } from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { Typography, Tooltip, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { ClassLevelOverrideOptions } from '../../../../types/adminConfig';

const StyledLink = styled('a')({
    color: '#90caf9',
    textDecoration: 'underline',
    '&:hover': {
        textDecoration: 'none'
    }
});

const FlexRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
});

const NewBadge = styled('span')(({ theme }) => ({
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    fontSize: '0.7rem',
    fontWeight: 600,
    padding: '2px 6px',
    borderRadius: '12px',
    textTransform: 'uppercase'
}));

const BaseIconStyle = {
    fontSize: '18px',
    marginLeft: '4px',
    cursor: 'pointer'
};

const LockIconStyled = styled(LockIcon)(({ theme }) => ({
    ...BaseIconStyle,
    color: theme.palette.primary.main
}));

const LockOpenIconPrimary = styled(LockOpenIcon)(({ theme }) => ({
    ...BaseIconStyle,
    transform: 'scaleX(-1)',
    color: theme.palette.primary.main
}));

const LockOpenIconGrey = styled(LockOpenIcon)(({ theme }) => ({
    ...BaseIconStyle,
    transform: 'scaleX(-1)',
    color: theme.palette.grey[500]
}));

type LabelWithTooltipProps = {
    label: string;
    tooltip?: string;
    tooltipUrl?: string;
    lockStatus?: ClassLevelOverrideOptions;
    lockStatusTooltipUrl?: string;
    onLockClicked?: () => void;
    showNewLabel?: boolean;
};

type LockStatusIconProps = {
    status: ClassLevelOverrideOptions;
    onClick?: () => void;
};

const LockStatusIcon = ({ status, onClick }: LockStatusIconProps) => {
    switch (status) {
        case ClassLevelOverrideOptions.NoOne:
            return <LockIconStyled onClick={onClick} />;
        case ClassLevelOverrideOptions.AdminsOnly:
            return <LockOpenIconPrimary onClick={onClick} />;
        case ClassLevelOverrideOptions.AdminsAndInstructors:
            return <LockOpenIconGrey onClick={onClick} />;
        default:
            return null;
    }
};

const parseTooltipWithLink = (text: string, url?: string) => {
    const parts = text.split(/<LINK>|<\/LINK>/);
    return (
        <Typography variant="body2" sx={{ margin: 0, lineHeight: 1 }}>
            {parts.map((part, i) =>
                i === 1 ? (
                    <StyledLink key={i} href={url} target="_blank" rel="noopener noreferrer">
                        {part}
                    </StyledLink>
                ) : (
                    <span key={i}>{part}</span>
                )
            )}
        </Typography>
    );
};

const LabelWithTooltip: FC<LabelWithTooltipProps> = ({
    label,
    tooltip,
    tooltipUrl,
    lockStatus,
    lockStatusTooltipUrl,
    onLockClicked,
    showNewLabel = false
}) => {
    const { t } = useTranslation('admin_settings');

    return (
        <FlexRow>
            <Typography>{label}</Typography>

            {showNewLabel && <NewBadge>{t('label-with-tooltip.new')}</NewBadge>}

            {lockStatus && (
                <Tooltip title={lockStatusTooltipUrl} placement="top">
                    <span>
                        <LockStatusIcon status={lockStatus} onClick={onLockClicked} />
                    </span>
                </Tooltip>
            )}
            {tooltip && (
                <Tooltip title={parseTooltipWithLink(tooltip, tooltipUrl)} placement="top">
                    <InfoOutlinedIcon fontSize="small" color="primary" />
                </Tooltip>
            )}
        </FlexRow>
    );
};

export default LabelWithTooltip;
